import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAdminStats } from '@/hooks/useAdmin';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Users, UserCheck, Clock, FileQuestion, FileText, Calendar, Loader2, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';

const statCards = [
  { key: 'activeMembers', label: 'Active Members', icon: UserCheck, color: 'text-green-500', href: '/admin/users?tab=approved' },
  { key: 'pendingMembers', label: 'Pending Members', icon: Clock, color: 'text-yellow-500', href: '/admin/users?tab=pending' },
  { key: 'applications', label: 'Applications', icon: FileQuestion, color: 'text-blue-500', href: '/admin/users?tab=applications' },
  { key: 'posts', label: 'Total Posts', icon: FileText, color: 'text-purple-500', href: null },
  { key: 'events', label: 'Events', icon: Calendar, color: 'text-orange-500', href: null },
] as const;

export default function AdminDashboard() {
  const { data: stats, isLoading } = useAdminStats();
  const [syncing, setSyncing] = useState(false);
  const { toast } = useToast();

  const handleHeartbeatSync = async () => {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('sync-heartbeat');

      if (error) throw error;

      const result = data as {
        members: { created: number; updated: number; skipped: number };
        posts: { imported: number; skipped: number };
        comments: { imported: number; skipped: number };
        events: { imported: number; updated: number; skipped: number };
        errors: string[];
      };

      const parts = [];
      if (result.members.created || result.members.updated) {
        parts.push(`Members: ${result.members.created} created, ${result.members.updated} updated`);
      }
      if (result.posts.imported) {
        parts.push(`Posts: ${result.posts.imported} imported`);
      }
      if (result.comments?.imported) {
        parts.push(`Comments: ${result.comments.imported} imported`);
      }
      if (result.events?.imported || result.events?.updated) {
        parts.push(`Events: ${result.events.imported} imported, ${result.events.updated} updated`);
      }

      toast({
        title: 'Heartbeat Sync Complete',
        description: parts.join('. ') || 'No new data to import.',
      });

      if (result.errors.length > 0) {
        console.warn('Sync errors:', result.errors);
      }
    } catch (error: any) {
      toast({
        title: 'Sync Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold text-foreground">Dashboard</h1>
        <p className="text-muted-foreground text-sm">
          Overview of your QBN community
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {statCards.map((stat) => {
            const cardContent = (
              <Card className={stat.href ? 'cursor-pointer hover:bg-muted/50 transition-colors' : ''}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{stat.label}</CardTitle>
                  <stat.icon className={`h-4 w-4 ${stat.color}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {stats?.[stat.key]?.toLocaleString() || 0}
                  </div>
                </CardContent>
              </Card>
            );

            return stat.href ? (
              <Link key={stat.key} to={stat.href}>
                {cardContent}
              </Link>
            ) : (
              <div key={stat.key}>{cardContent}</div>
            );
          })}
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common administrative tasks</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <a
              href="/admin/users"
              className="block p-3 rounded-lg hover:bg-muted transition-colors"
            >
              <div className="font-medium">Manage Users</div>
              <div className="text-sm text-muted-foreground">
                View and update user roles
              </div>
            </a>
            <a
              href="/admin/invites"
              className="block p-3 rounded-lg hover:bg-muted transition-colors"
            >
              <div className="font-medium">Generate Invite Codes</div>
              <div className="text-sm text-muted-foreground">
                Create new invite codes for members
              </div>
            </a>
            <a
              href="/admin/channels"
              className="block p-3 rounded-lg hover:bg-muted transition-colors"
            >
              <div className="font-medium">Manage Channels</div>
              <div className="text-sm text-muted-foreground">
                Create and organize discussion channels
              </div>
            </a>
            <div className="pt-2 border-t">
              <Button
                onClick={handleHeartbeatSync}
                disabled={syncing}
                variant="outline"
                className="w-full justify-start"
              >
                {syncing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                {syncing ? 'Syncing Heartbeat...' : 'Sync from Heartbeat'}
              </Button>
              <p className="text-xs text-muted-foreground mt-1 px-1">
                Import members, posts, comments, and events from Heartbeat
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Community Health</CardTitle>
            <CardDescription>Recent activity metrics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Active Users (7d)</span>
                <span className="font-medium">--</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">New Posts (7d)</span>
                <span className="font-medium">--</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Event RSVPs (7d)</span>
                <span className="font-medium">--</span>
              </div>
              <p className="text-xs text-muted-foreground pt-2">
                Detailed analytics coming soon
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
